import { analytics } from '@/features/analytics/events'
import { useMissionsUrl } from '@/features/missions/context/MissionsUrlProvider'
import { routes } from '@/pages'
import { Button, Text } from '@/ui-kit'
import { formatNumber } from '@/utils'
import { plural } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { isProlificMode } from '../prolific/session'
import { LinkedMissions } from './LinkedMissions'

type NectarTasksAllDoneProps = {
  name: string
  rewardConfig?: {
    type: string
    amount: number
  }
  projectId?: string
}

export const NectarTasksAllDone = ({ name, rewardConfig, projectId }: NectarTasksAllDoneProps) => {
  const { missionsUrl } = useMissionsUrl()
  const navigate = useNavigate()
  const prolificMode = isProlificMode()
  const onBackToMissions = () => navigate(missionsUrl)
  const reward = rewardConfig
    ? rewardConfig.type === 'usd'
      ? `$${formatNumber(rewardConfig.amount, 2)}`
      : plural(rewardConfig.amount, { one: '# point', other: '# points' })
    : ''

  useEffect(() => {
    if (projectId) analytics.missionAllTasksDone(projectId)
  }, [projectId])

  return (
    <div className="mt-20">
      <div className="max-mobile:px-2 relative mx-auto flex w-[780px] flex-col items-center justify-center gap-6 rounded-2xl border border-solid border-gray-100 px-10 py-12 text-center max-md:w-full max-md:gap-4">
        <img
          src={'/images/cup.webp'}
          alt={name}
          className="absolute top-0 left-0 h-[300px] w-[300px] translate-x-[-30%] translate-y-[-35%] max-md:h-[200px] max-md:w-[200px] max-md:translate-y-[-50%]"
        />
        <Text type="h5" className="text-gray-70">
          <Trans>All Done!</Trans>
        </Text>
        <div className="flex max-w-[450px] flex-col items-center justify-center">
          <Text type="body1-regular" className="text-gray-80">
            <Trans>Your work has been submitted and is now waiting for review.</Trans>
          </Text>
          {!!rewardConfig?.amount && (
            <Text type="body1-regular" className="text-gray-80">
              <Trans>
                After verification and approval, you can earn up to{' '}
                <span className={rewardConfig?.type === 'usd' ? 'text-yellow' : 'text-green'}>{reward}</span>.
                Processing times may vary.
              </Trans>
            </Text>
          )}
          <div className="text-body-3 text-gray-80 mt-8 max-w-[300px]">
            <Trans>Keep an eye on your mission activity or continue exploring other missions meantime.</Trans>
          </div>
        </div>
        {!prolificMode && (
          <div className="flex flex-col items-center justify-center gap-2">
            <div className="flex gap-2">
              <Button variant="bordered" size="small" onClick={() => navigate(routes.activity.path)}>
                <Trans>Track Activity</Trans>
              </Button>
              <Button size="small" onClick={onBackToMissions}>
                <Trans>Back to Missions</Trans>
              </Button>
            </div>
          </div>
        )}
        {!prolificMode && projectId && <LinkedMissions projectId={projectId} />}
      </div>
    </div>
  )
}
